import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Skeleton,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import theme from '../UserDashboard/theme';
import { getUserRobots, updateUserRobotIP } from '../services/userRobotApi';
import RobotDetailView from './RobotDetailView';

const accent = theme.colors.brandDark;
const accentLight = 'rgba(37, 99, 235, 0.08)';

const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;

/** Extract dotted IPv4 from `ws://<ip>:<port>`. */
function parseIpv4FromWsUrl(wsUrl) {
  if (!wsUrl || typeof wsUrl !== 'string') return '';
  const m = String(wsUrl).trim().match(/^ws:\/\/([^:/]+)(?::\d+)?$/i);
  return m ? m[1] : '';
}

function getStatusConfig(status) {
  const s = (status || '').toLowerCase();
  if (s === 'active') {
    return {
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />,
      bg: 'rgba(5, 150, 105, 0.12)',
      color: theme.colors.accentSuccess,
    };
  }
  if (s === 'maintenance') {
    return {
      icon: <BuildOutlinedIcon sx={{ fontSize: 16 }} />,
      bg: 'rgba(217, 119, 6, 0.12)',
      color: theme.colors.accentWarning,
    };
  }
  if (s === 'inactive') {
    return {
      icon: <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />,
      bg: 'rgba(217, 119, 6, 0.12)',
      color: theme.colors.accentWarning,
    };
  }
  if (s === 'decommissioned') {
    return {
      icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
      bg: 'rgba(220, 38, 38, 0.12)',
      color: theme.colors.accentDanger,
    };
  }
  return {
    icon: <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />,
    bg: accentLight,
    color: accent,
  };
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function MetaRow({ label, value }) {
  return (
    <Box sx={metaRowStyles.row}>
      <Typography sx={metaRowStyles.label}>{label}</Typography>
      <Typography sx={metaRowStyles.value} title={value}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

function RobotCard({ robot, getStatusConfig, onSelect, onEditIp }) {
  const statusConfig = getStatusConfig(robot.status);
  const locationLine = [robot.location, robot.subLocation].filter(Boolean).join(' · ');

  return (
    <Card
      elevation={0}
      sx={styles.card}
      onClick={() => onSelect(robot)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(robot);
        }
      }}
    >
      <Box sx={styles.cardLeft}>
        <CardContent sx={styles.cardContent}>
          <Typography sx={styles.robotName}>{robot.username || '—'}</Typography>
          <Box sx={styles.metaGrid}>
            <MetaRow label="Robot ID" value={robot.robotId} />
            <MetaRow label="Model" value={robot.model} />
            <MetaRow label="Serial" value={robot.serialNumber} />
            <MetaRow label="WebSocket URL" value={robot.IPAddress} />
            <MetaRow label="Location" value={locationLine || '—'} />
            <MetaRow label="Amps" value={robot.amps != null ? String(robot.amps) : '—'} />
            <MetaRow label="Initialized" value={formatDate(robot.robotInitializeDate)} />
            <MetaRow label="Last maintenance" value={formatDate(robot.lastMaintenanceDate)} />
          </Box>
          {!robot.image && (
            <Chip
              size="small"
              icon={statusConfig.icon}
              label={robot.status || '—'}
              sx={{
                ...styles.statusChipInline,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            />
          )}
          <Box sx={styles.actions}>
            <Button
              startIcon={<EditOutlinedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onEditIp(robot);
              }}
              size="small"
              sx={styles.editButton}
            >
              Update IP
            </Button>
          </Box>
        </CardContent>
      </Box>
      <Box sx={styles.cardRight}>
        {robot.image ? (
          <>
            <CardMedia
              component="img"
              image={robot.image}
              alt={robot.username}
              sx={styles.cardImage}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <Chip
              size="small"
              icon={statusConfig.icon}
              label={robot.status || '—'}
              sx={{
                ...styles.statusChip,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            />
          </>
        ) : (
          <Box sx={styles.cardImagePlaceholder}>
            <SmartToyOutlinedIcon sx={styles.placeholderIcon} />
            <Chip
              size="small"
              icon={statusConfig.icon}
              label={robot.status || '—'}
              sx={{
                ...styles.statusChip,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
              }}
            />
          </Box>
        )}
      </Box>
    </Card>
  );
}

export default function Robots() {
  const [robots, setRobots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRobot, setEditRobot] = useState(null);
  const [ipDraft, setIpDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedRobot, setSelectedRobot] = useState(null);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const loadRobots = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    setFetchError('');
    try {
      const res = await getUserRobots();
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setRobots(list);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.message;
      if (status === 403) {
        setAccessDenied(true);
        setRobots([]);
      } else if (status === 404) {
        setRobots([]);
      } else {
        setRobots([]);
        setFetchError(message || 'Failed to load robots. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRobots();
  }, [loadRobots]);

  const handleOpenEdit = (robot) => {
    setEditRobot(robot);
    setIpDraft(parseIpv4FromWsUrl(robot.IPAddress));
    setEditDialogOpen(true);
  };

  const handleSaveIp = async () => {
    if (!editRobot?.robotId) return;
    const trimmed = ipDraft.trim();
    if (!trimmed) {
      showSnackbar('New IP address is required', 'error');
      return;
    }
    if (!IPV4_RE.test(trimmed)) {
      showSnackbar('Invalid IP address format', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await updateUserRobotIP(editRobot.robotId, trimmed);
      const msg = res.data?.message || 'IP address updated successfully';
      const updated = res.data?.data;
      if (updated && updated.robotId) {
        setRobots((prev) =>
          prev.map((r) => (r.robotId === updated.robotId ? { ...r, ...updated } : r)),
        );
        setSelectedRobot((prev) =>
          prev?.robotId === updated.robotId ? { ...prev, ...updated } : prev,
        );
      } else {
        await loadRobots();
      }
      showSnackbar(msg, 'success');
      setEditDialogOpen(false);
    } catch (err) {
      const message =
        err.response?.data?.message || 'Failed to update IP address. Please try again.';
      showSnackbar(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={styles.pageWrap}>
        <Box sx={styles.header}>
          <Skeleton variant="text" width={200} height={36} />
          <Skeleton variant="text" width={320} height={24} sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2].map((i) => (
            <Grid item key={i} xs={12} md={6}>
              <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (selectedRobot) {
    return (
      <Box sx={styles.pageWrap}>
        <RobotDetailView
          robot={selectedRobot}
          onBack={() => setSelectedRobot(null)}
        />
        {renderEditDialog()}
        {renderSnackbar()}
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>My robots</Typography>
        <Typography sx={styles.subtitle}>
          View robots assigned to your account and update connection IP addresses.
        </Typography>
      </Box>

      {accessDenied && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          Access denied. This page is only available for client users.
        </Alert>
      )}

      {fetchError && !accessDenied && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {fetchError}
        </Alert>
      )}

      {robots.length === 0 && !accessDenied && !fetchError ? (
        <Box sx={styles.emptyState}>
          <SmartToyOutlinedIcon sx={styles.emptyIcon} />
          <Typography sx={styles.emptyTitle}>No robots assigned</Typography>
          <Typography sx={styles.emptySubtitle}>
            There are no robots linked to your account yet. Contact your administrator if you
            expect robots to appear here.
          </Typography>
        </Box>
      ) : robots.length === 1 ? (
        <Box sx={styles.singleCardWrap}>
          <Box sx={styles.singleCardInner}>
            <RobotCard
              robot={robots[0]}
              getStatusConfig={getStatusConfig}
              onSelect={setSelectedRobot}
              onEditIp={handleOpenEdit}
            />
          </Box>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {robots.map((robot, index) => (
            <Grid item key={robot.robotId || robot._id || index} xs={12} md={6} lg={6}>
              <RobotCard
                robot={robot}
                getStatusConfig={getStatusConfig}
                onSelect={setSelectedRobot}
                onEditIp={handleOpenEdit}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {renderEditDialog()}
      {renderSnackbar()}
    </Box>
  );

  function renderEditDialog() {
    return (
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        PaperProps={{ sx: { ...styles.dialogPaper, maxWidth: 460 } }}
        sx={styles.dialogRoot}
      >
        <Box sx={styles.dialogEditHeader}>
          <Box sx={styles.dialogIconCircleEdit}>
            <EditOutlinedIcon sx={{ fontSize: 26, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={styles.dialogTitleMain}>Update robot IP</Typography>
            <Typography sx={styles.dialogTitleSub}>
              Enter IPv4 only. The WebSocket port from your current URL is kept automatically.
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={styles.dialogContentEdit}>
          {editRobot && (
            <Box sx={styles.editFields}>
              <Typography sx={styles.editFieldLabel}>Robot</Typography>
              <Typography sx={styles.editRobotId}>
                {editRobot.robotId} · {editRobot.username || '—'}
              </Typography>
              <Box sx={styles.editFieldWrap}>
                <Typography sx={styles.editFieldLabel}>Current WebSocket URL</Typography>
                <Typography sx={styles.currentWsUrl}>{editRobot.IPAddress || '—'}</Typography>
              </Box>
              <Box sx={styles.editFieldWrap}>
                <Typography sx={styles.editFieldLabel}>New IPv4 address</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  value={ipDraft}
                  onChange={(e) => setIpDraft(e.target.value)}
                  sx={styles.editFieldInput}
                  variant="outlined"
                  placeholder="e.g. 192.168.1.55"
                  disabled={saving}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={styles.dialogActions}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={saving}
            sx={styles.dialogCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveIp}
            variant="contained"
            disabled={saving}
            sx={styles.dialogUpdate}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  function renderSnackbar() {
    return (
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  }
}

const metaRowStyles = {
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 1.5,
    py: 0.75,
    borderBottom: '1px solid rgba(0,0,0,0.04)',
    '&:last-of-type': { borderBottom: 'none' },
  },
  label: {
    fontSize: '0.8125rem',
    color: theme.colors.textSecondary,
    fontWeight: 500,
    flexShrink: 0,
  },
  value: {
    fontSize: '0.875rem',
    color: theme.colors.textPrimary,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    maxWidth: '65%',
    textAlign: 'right',
  },
};

const styles = {
  pageWrap: {
    width: '100%',
    maxWidth: 1280,
    mx: 'auto',
    boxSizing: 'border-box',
  },
  header: {
    mb: 3,
  },
  title: {
    fontWeight: 700,
    color: theme.colors.textPrimary,
    fontSize: { xs: '1.25rem', md: '1.5rem' },
  },
  subtitle: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    mt: 0.5,
  },
  emptyState: {
    textAlign: 'center',
    py: 8,
    px: 2,
    bgcolor: theme.colors.bgPrimary,
    borderRadius: 3,
    border: `1px solid ${theme.colors.borderDefault}`,
    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
  },
  emptyIcon: {
    fontSize: 56,
    color: '#cbd5e1',
    mb: 2,
  },
  emptyTitle: {
    color: theme.colors.textSecondary,
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  emptySubtitle: {
    color: '#94a3b8',
    fontSize: '0.875rem',
    mt: 1,
    maxWidth: 420,
    mx: 'auto',
  },
  singleCardWrap: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  singleCardInner: {
    width: '100%',
    maxWidth: 720,
  },
  card: {
    borderRadius: 3,
    overflow: 'hidden',
    cursor: 'pointer',
    bgcolor: theme.colors.bgPrimary,
    boxShadow: 'none',
    border: `1px solid ${theme.colors.borderDefault}`,
    minHeight: 320,
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
  },
  cardLeft: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  cardRight: {
    position: 'relative',
    width: { xs: '100%', sm: 280 },
    minHeight: { xs: 240, sm: 280 },
    flexShrink: 0,
    bgcolor: theme.colors.bgTertiary,
  },
  cardImage: {
    width: '100%',
    height: '100%',
    minHeight: 280,
    objectFit: 'cover',
  },
  cardImagePlaceholder: {
    width: '100%',
    height: '100%',
    minHeight: 280,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    bgcolor: theme.colors.bgTertiary,
  },
  placeholderIcon: {
    fontSize: 72,
    color: '#cbd5e1',
  },
  statusChip: {
    position: 'absolute',
    top: 12,
    right: 12,
    fontWeight: 600,
    fontSize: '0.75rem',
    '& .MuiChip-icon': { color: 'inherit' },
  },
  statusChipInline: {
    mt: 1,
    fontWeight: 600,
    fontSize: '0.75rem',
    '& .MuiChip-icon': { color: 'inherit' },
  },
  cardContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    py: 2.5,
    px: 3,
    '&:last-child': { pb: 2.5 },
  },
  robotName: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.colors.textPrimary,
    mb: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaGrid: {
    flex: 1,
    minHeight: 0,
  },
  actions: {
    display: 'flex',
    gap: 1.5,
    mt: 2,
    pt: 2,
    borderTop: '1px solid rgba(0,0,0,0.06)',
  },
  editButton: {
    textTransform: 'none',
    color: accent,
    fontSize: '0.875rem',
    '&:hover': { bgcolor: accentLight },
  },
  dialogRoot: {
    '& .MuiBackdrop-root': {
      bgcolor: 'rgba(15, 23, 42, 0.4)',
      backdropFilter: 'blur(2px)',
    },
  },
  dialogPaper: {
    borderRadius: 4,
    p: 0,
    maxWidth: 420,
    bgcolor: '#ffffff',
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
    overflow: 'hidden',
  },
  dialogEditHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    px: 3,
    pt: 3,
    pb: 2,
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    bgcolor: theme.colors.bgSecondary,
  },
  dialogIconCircleEdit: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    bgcolor: accent,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.35)',
  },
  dialogTitleMain: {
    fontWeight: 700,
    fontSize: '1.25rem',
    color: theme.colors.textPrimary,
    lineHeight: 1.3,
  },
  dialogTitleSub: {
    fontSize: '0.875rem',
    color: theme.colors.textSecondary,
    mt: 0.5,
    lineHeight: 1.4,
  },
  dialogContentEdit: {
    px: 3,
    pt: 2.5,
    pb: 2,
    overflow: 'visible',
  },
  dialogActions: {
    px: 3,
    py: 2.5,
    gap: 1.5,
    borderTop: '1px solid rgba(0,0,0,0.06)',
    bgcolor: theme.colors.bgSecondary,
  },
  dialogCancel: {
    textTransform: 'none',
    color: theme.colors.textSecondary,
    fontSize: '0.9375rem',
    fontWeight: 600,
    borderRadius: 2,
    px: 2.5,
    '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
  },
  dialogUpdate: {
    textTransform: 'none',
    fontSize: '0.9375rem',
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1.25,
    bgcolor: accent,
    boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
    '&:hover': {
      bgcolor: theme.colors.brandPrimary,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
    },
  },
  editFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  editFieldWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },
  editFieldLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.colors.textPrimary,
  },
  editRobotId: {
    fontSize: '0.9375rem',
    color: theme.colors.textSecondary,
    mb: 0.5,
  },
  currentWsUrl: {
    fontSize: '0.875rem',
    color: theme.colors.textSecondary,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  editFieldInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      bgcolor: '#ffffff',
      '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
      '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '2px' },
    },
  },
};
